// Display-only animation on top of the sampled view (visuals §9.1, §9.2, §6; backlog C-1/C-2 look):
// - drawn-radius easing: `r_draw += (r_interp − r_draw) × easeFactor(dt, 158 ms)` (agar eases 7 %
//   of the remaining gap per 60 Hz frame; we keep it frame-rate independent). New entities start
//   at full size. Purely visual: collision is the server's;
// - the eat effect: the store slides an eaten record into its eater and shrinks it to 0 over
//   100 ms; we draw it at r × (1 − 0.3 t) with α = 1 − t instead (agar's slide-and-fade);
// - classic pellets: no pop-in (they appear at full size) and the 15 → 25 u draw-radius mapping.
//
// If the store ever writes `alpha` itself (backlog C-1), `rec.r` is taken as the final drawn
// radius and `rec.alpha` as the opacity (no double effect).

import { easeFactor, KIND, type Kind } from '@ballio/shared';
import { ENTITY_EFFECT, type EntityRecord } from '../game/world-store.js';

export const DISPLAY = Object.freeze({
  /** Drawn-radius easing half-life (ms): agar's 7 %/frame at 60 fps. */
  radiusHalfLifeMs: 158,
  /** An eaten entity shrinks to this share of its radius by the end of the effect (× (1 − 0.3)). */
  eatenShrink: 0.3,
  /** Easing state is dropped after this long unseen. */
  forgetMs: 1000,
  /** Classic pellet draw radius: server r 10 → 15 u, r 22.36 (mass 5) → 25 u. */
  pelletBaseR: 10,
  pelletDrawBase: 15,
  pelletDrawSlope: 10 / 12.36,
});

/** Half-life (ms) equivalent to easing a share `k` of the gap per frame at `fps` (visuals §0). */
export function perFrameHalfLifeMs(k: number, fps = 60): number {
  if (!(k > 0) || k >= 1) return 0;
  return ((1000 / fps) * Math.log(0.5)) / Math.log(1 - k);
}

/** One easing step of a drawn radius toward `target` over `dtMs`. */
export function easeRadius(
  current: number,
  target: number,
  dtMs: number,
  halfLifeMs: number = DISPLAY.radiusHalfLifeMs,
): number {
  if (!Number.isFinite(current)) return target;
  return current + (target - current) * easeFactor(dtMs > 0 ? dtMs : 0, halfLifeMs);
}

/** Classic pellet draw radius from the server radius (visuals §6): 15 + (r − 10) × 10 / 12.36. */
export function pelletDrawRadius(r: number): number {
  if (!(r > 0)) return 0;
  const d = DISPLAY.pelletDrawBase + (r - DISPLAY.pelletBaseR) * DISPLAY.pelletDrawSlope;
  return d > 1 ? d : 1;
}

/** The eaten look at progress t: radius share and opacity (visuals §9.1). */
export function eatenRadiusFactor(t: number): number {
  const c = t <= 0 ? 0 : t >= 1 ? 1 : t;
  return 1 - DISPLAY.eatenShrink * c;
}

export function eatenAlpha(t: number): number {
  const c = t <= 0 ? 0 : t >= 1 ? 1 : t;
  return 1 - c;
}

/** True when the store already applies the display alpha (backlog C-1 landed). */
function storeAlpha(rec: EntityRecord): number | undefined {
  const a = (rec as { alpha?: unknown }).alpha;
  return typeof a === 'number' ? a : undefined;
}

/**
 * The record's radius before the store's own effect scaling: an eaten record's pre-shrink radius
 * (`r / (1 − t)`), a popping-in pellet's full radius (`noPopIn`), else `rec.r`.
 */
export function baseRadius(rec: EntityRecord, noPopIn: boolean): number {
  if (storeAlpha(rec) !== undefined) return rec.r;
  const t = rec.effectT;
  if (rec.effect === ENTITY_EFFECT.EATEN) {
    return t > 0 && t < 1 ? rec.r / (1 - t) : rec.r;
  }
  if (noPopIn && rec.effect === ENTITY_EFFECT.POP_IN) {
    if (t > 0) return rec.r / t;
    const n = rec.hist?.newest?.();
    return n ? n.r : rec.r;
  }
  return rec.r;
}

/** The record's display opacity (eaten fade), before ghost alpha. */
export function displayAlpha(rec: EntityRecord): number {
  const a = storeAlpha(rec);
  if (a !== undefined) return a;
  return rec.effect === ENTITY_EFFECT.EATEN ? eatenAlpha(rec.effectT) : 1;
}

/** The final drawn radius from an (eased) base radius: the eat shrink applied. */
export function effectRadius(rec: EntityRecord, base: number): number {
  if (storeAlpha(rec) !== undefined) return base;
  return rec.effect === ENTITY_EFFECT.EATEN ? base * eatenRadiusFactor(rec.effectT) : base;
}

interface EaseState {
  r: number;
  kind: Kind;
  seenMs: number;
}

/** Per-entity drawn radii (eased toward the sampled radius), keyed by id. */
export class RadiusEaser {
  private readonly states = new Map<number, EaseState>();
  private readonly pool: EaseState[] = [];
  private lastSweepMs = 0;
  halfLifeMs: number = DISPLAY.radiusHalfLifeMs;

  get size(): number {
    return this.states.size;
  }

  /**
   * The drawn radius of entity `id` this frame: `target` on first sight (new entities start at
   * full size) or after a kind change (an id reused), else eased toward `target` over `dtMs`.
   */
  ease(id: number, kind: Kind, target: number, nowMs: number, dtMs: number): number {
    let st = this.states.get(id);
    if (!st || st.kind !== kind || nowMs - st.seenMs > DISPLAY.forgetMs) {
      if (!st) {
        st = this.pool.pop() ?? { r: target, kind, seenMs: nowMs };
        this.states.set(id, st);
      }
      st.r = target;
      st.kind = kind;
      st.seenMs = nowMs;
      return target;
    }
    st.r = easeRadius(st.r, target, dtMs, this.halfLifeMs);
    st.seenMs = nowMs;
    return st.r;
  }

  /** Forgets entities unseen for a second (call once per frame). */
  sweep(nowMs: number): void {
    if (nowMs - this.lastSweepMs < 500) return;
    this.lastSweepMs = nowMs;
    for (const [id, st] of this.states) {
      if (nowMs - st.seenMs > DISPLAY.forgetMs) {
        this.states.delete(id);
        this.pool.push(st);
      }
    }
  }

  clear(): void {
    for (const st of this.states.values()) this.pool.push(st);
    this.states.clear();
  }
}

/** Kinds whose drawn radius is eased (pellets do not change size). */
export function easesRadius(kind: Kind): boolean {
  return (
    kind === KIND.CELL || kind === KIND.VIRUS || kind === KIND.EJECTED || kind === KIND.SPAWNER
  );
}
